/**
 * SR VARIANT-LEVEL PRE-ORDER
 *
 * Keeps the PDP pre-order UI in sync with the variant selected in <variant-picker>.
 * Per-variant data is printed by snippets/sr-variant-preorder.liquid:
 *   <script type="application/json" data-sr-preorder-data>
 *     { "strings": { "addToCart": "...", "preorder": "..." },
 *       "variants": { "<variantId>": { "preorder": true, "message": "..." } } }
 *   </script>
 *
 * Pre-order variant  -> "Pre-order" label on main + sticky buttons, timeline banner shown,
 *                       wishlist hidden.
 * Any other variant  -> default flow (Add to cart / Sold out / Notify me), handled by
 *                       variant-picker.js and sticky-atc.js as before.
 *
 * Emits `sr:preorder:change` on the product root with { variant, isPreorder, message }.
 */
(function () {
  if (window.SRVariantPreorder) return;

  const ROOT_SELECTOR = '.js-product';

  function getRoot(el) {
    return (el && el.closest && el.closest(ROOT_SELECTOR)) || null;
  }

  function readData(root) {
    const el = root && root.querySelector('script[data-sr-preorder-data]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[SR preorder] invalid data', e);
      return null;
    }
  }

  function readVariants(variantPicker) {
    const el = variantPicker && variantPicker.querySelector('script[type="application/json"]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent).variants || [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Current variant of a product root: the variant-picker instance first,
   * then the product form's hidden [name="id"] input.
   */
  function getCurrentVariant(root) {
    const vp = root.querySelector('variant-picker');
    if (vp && vp.variant) return vp.variant;

    const input = root.querySelector('form.js-product-form input[name="id"]');
    const id = input && input.value;
    if (!id) return null;
    return readVariants(vp).find((v) => String(v.id) === String(id)) || null;
  }

  function setButtonLabel(btn, label, variantAvailable) {
    if (!btn) return;
    // variant-picker.js / sticky-atc.js read this attribute on every update
    btn.dataset.addToCartText = label;
    // Only rewrite the visible label when the button is in its purchasable state;
    // sold-out / unavailable text is owned by the default flow.
    if (variantAvailable) btn.textContent = label;
  }

  function apply(root, variant) {
    if (!root) return;
    const data = readData(root);
    if (!data) return;

    const entry = variant ? (data.variants || {})[String(variant.id)] : null;
    const available = !!(variant && variant.available);
    // A sold-out pre-order variant falls back to the default sold out / notify me flow
    const isPreorder = !!(entry && entry.preorder && available);
    const message = (isPreorder && entry.message) || '';
    const strings = data.strings || {};
    const label = isPreorder ? strings.preorder : strings.addToCart;

    // Main add-to-cart button
    const mainBtn = root.querySelector('form.js-product-form button.add-to-cart[name="add"]');
    setButtonLabel(mainBtn, label, available);
    if (mainBtn) mainBtn.classList.toggle('is-preorder', isPreorder);

    // Sticky add-to-cart button (rendered outside product-form)
    document.querySelectorAll('sticky-atc button.add-to-cart[name="add"]').forEach((btn) => {
      setButtonLabel(btn, label, available);
      btn.classList.toggle('is-preorder', isPreorder);
    });

    // Estimated timeline banner
    root.querySelectorAll('.js-sr-preorder-banner').forEach((banner) => {
      const msgEl = banner.querySelector('.js-sr-preorder-message');
      if (msgEl) msgEl.innerHTML = message;
      banner.hidden = !message;
    });

    // Wishlist stays hidden for pre-order variants (previous tag behaviour)
    root.querySelectorAll('.sr-addtowishlist').forEach((el) => {
      el.hidden = isPreorder;
    });

    root.classList.toggle('is-preorder-variant', isPreorder);
    root.dispatchEvent(
      new CustomEvent('sr:preorder:change', {
        bubbles: true,
        detail: { variant: variant || null, isPreorder, message }
      })
    );
  }

  function syncAll() {
    document.querySelectorAll('variant-picker').forEach((vp) => {
      const root = getRoot(vp);
      if (root) apply(root, getCurrentVariant(root));
    });
  }

  // Fired by variant-picker.js after every selection change
  document.addEventListener('on:variant:change', (evt) => {
    const root = getRoot(evt.target);
    if (!root) return;
    const variant = evt.detail ? evt.detail.variant : getCurrentVariant(root);
    apply(root, variant);
  });

  // Colour dots swap the whole product-form for another product
  document.addEventListener('product:color-dot:change', () => {
    setTimeout(syncAll, 0);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAll);
  } else {
    syncAll();
  }
  // Re-sync once everything (sticky ATC, apps) has initialised
  window.addEventListener('load', syncAll);

  window.SRVariantPreorder = { apply, syncAll, getCurrentVariant };
})();
